import { useState, useMemo } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

export function PasswordInput({
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  required = false,
  labelKey = "pwd.newPassword",
  confirmLabelKey = "pwd.confirmPassword",
}) {
  const { t } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const rules = useMemo(() => {
    const val = password || "";
    return [
      { key: "minLength", pass: val.length >= 8 },
      { key: "uppercase", pass: /[A-Z]/.test(val) },
      { key: "lowercase", pass: /[a-z]/.test(val) },
      { key: "number", pass: /[0-9]/.test(val) },
      { key: "special", pass: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(val) },
    ];
  }, [password]);

  const passedCount = rules.filter((r) => r.pass).length;
  const hasInput = (password || "").length > 0;

  const strengthLevel = passedCount <= 1 ? 0 : passedCount <= 2 ? 1 : passedCount <= 3 ? 2 : passedCount <= 4 ? 3 : 4;
  const strengthColors = ["bg-red-500", "bg-orange-500", "bg-amber-500", "bg-blue-500", "bg-emerald-500"];
  const strengthLabels = ["pwd.veryWeak", "pwd.weak", "pwd.fair", "pwd.strong", "pwd.veryStrong"];

  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const showMismatch = confirmPassword && !passwordsMatch;

  return (
    <div className="space-y-3">
      {/* Password Field */}
      <div className="space-y-2">
        <Label data-testid="password-label">{t(labelKey)}</Label>
        <div className="relative">
          <Input
            data-testid="password-input"
            type={showPassword ? "text" : "password"}
            // new-password on both: without it a password manager offers the
            // one already saved for this site, which is the value being
            // replaced, and never offers to generate one.
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("pwd.placeholder")}
            required={required}
            className="border-slate-200 pr-10"
          />
          <button
            type="button"
            data-testid="toggle-password-visibility"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={t("pwd.toggleVisibility")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Strength Bar */}
      {hasInput && (
        <div className="space-y-1.5" data-testid="password-strength-section">
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                data-testid={`strength-bar-${i}`}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i <= strengthLevel ? strengthColors[strengthLevel] : "bg-slate-200"
                }`}
              />
            ))}
          </div>
          <p className="text-xs text-slate-500" data-testid="strength-label">
            {t(strengthLabels[strengthLevel])}
          </p>
        </div>
      )}

      {/* Validation Checklist */}
      {hasInput && (
        <div className="space-y-1" data-testid="password-rules-checklist">
          {rules.map((rule) => (
            <div
              key={rule.key}
              data-testid={`rule-${rule.key}`}
              className={`flex items-start gap-2 text-xs transition-colors ${
                rule.pass ? "text-emerald-600" : "text-slate-400"
              }`}
            >
              {rule.pass ? (
                <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              ) : (
                <X className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              )}
              <span className="min-w-0">{t(`pwd.rule.${rule.key}`)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Confirm Password Field */}
      <div className="space-y-2">
        <Label data-testid="confirm-password-label">{t(confirmLabelKey)}</Label>
        <div className="relative">
          <Input
            data-testid="confirm-password-input"
            type={showConfirm ? "text" : "password"}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t("pwd.confirmPlaceholder")}
            required={required}
            className={`border-slate-200 pr-10 ${
              showMismatch ? "border-red-300 focus-visible:ring-red-400" : ""
            } ${passwordsMatch ? "border-emerald-300 focus-visible:ring-emerald-400" : ""}`}
          />
          <button
            type="button"
            data-testid="toggle-confirm-visibility"
            onClick={() => setShowConfirm(!showConfirm)}
            aria-label={t("pwd.toggleVisibility")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        {showMismatch && (
          <p className="text-xs text-red-500" data-testid="password-mismatch-error">
            {t("pwd.mismatch")}
          </p>
        )}
        {passwordsMatch && (
          <p className="text-xs text-emerald-600 flex items-center gap-1" data-testid="password-match-success">
            <Check className="w-3.5 h-3.5 shrink-0" />
            {t("pwd.match")}
          </p>
        )}
      </div>
    </div>
  );
}
